<template>
    <div class="patient-dashboard-view" :class="{ 'show-all-cards': showAllCards }">
        <!-- Dynamic styles for dashboard cards - prevents Flash of Content -->
        <component :is="'style'" v-if="dashboardStyleCSS">
            {{ dashboardStyleCSS }}
        </component>

        <card title="Patient Dashboard" class="titlebar">
            <template #header-right>
                <div class="d-flex" style="gap: 10px;">
                    <b-button v-if="hasHiddenCards" @click="toggleShowAllCards"
                        :variant="showAllCards ? 'primary' : 'outline-primary'" size="sm" v-b-tooltip.hover
                        title="Temporarily show hidden cards">
                        <i :class="showAllCards ? 'fas fa-eye-slash' : 'fas fa-eye'" class="mr-1"></i>
                        All Cards
                    </b-button>
                    <b-button @click="openCustomizeModal" variant="primary" size="sm">
                        <i class="fas fa-cog mr-2"></i>
                        Customize Dashboard
                    </b-button>
                </div>
            </template>
            <b-row align-v="end">
                <b-col>
                    <SearchComponent v-model="patient" :label="`Search`" search-u-r-l="/Patient"
                        :shouldFocus="searchShouldFocus" />
                </b-col>
                <b-col cols="1">
                    <b-button block class="my-0" variant="danger" @click="clearClick">
                        Clear
                    </b-button>
                </b-col>
            </b-row>
        </card>

        <b-row v-if="patient.id">
            <b-col cols="4">
                <card class="patient-name glass-pane">
                    <b-spinner v-if="loadingPatient" />
                    <template v-else>
                        <div class="mb-1">

                            <router-link :to="{
                                name: 'PatientDetails',
                                params: { id: patient.id },
                            }" class="d-flex align-items-center pull-right mt-1">
                                <span>Edit</span> <i class="fa fa-edit ml-1" />
                            </router-link>

                            <h4 class="card-subtitle" style="font-weight: 500;">
                                {{ patientName }}
                            </h4>

                            <div class="text-muted">
                                {{ patient.dateOfBirth | formatDate("L", false) }}
                                &middot;
                                {{ patient.fullAddress }}
                            </div>
                        </div>

                        <div class="mt-2">
                            <PatientActionsBar ref="patientActionsBar" v-if="patient" :patient="patient" :prescriptions="prescriptions"
                                @change-follow-up-cases="fetchFollowUpCases"
                                @update-consultations="updateConsultationsList"
                                @update-patient="refreshPatientData"
                                @medsync-enrollment-completed="loadAutoRefillData" />
                        </div>

                    </template>
                </card>

                <!-- Patient Demographics -->
                <card data-card-id="patient-demographics"
                    :data-card-visible="getCardVisibility('patient-demographics')">
                    <PatientDemographicsComponent v-model="patient" />
                </card>

                <!-- Eligible Programs -->
                <card data-card-id="eligible-programs" :data-card-visible="getCardVisibility('eligible-programs')">
                    <h4 class="card-subtitle">Eligible Programs</h4>
                    <PatientProgramEligibilityComponent v-model="patient" />
                </card>

                <!-- Notes -->
                <card data-card-id="notes" :data-card-visible="getCardVisibility('notes')">
                    <!-- Notes -->
                    <h4 class="card-subtitle">Notes</h4>
                    <NotesForm v-if="patient" :entityId="+patient.id" :entityType="patientEntityType"
                        @saved="refreshNotesList" />
                    <NotesList ref="patientNotesList" :entityId="+patient.id" :entityType="patientEntityType"
                        :pageSize="5" />
                </card>

                <!-- Subscriptions -->
                <PatientStripeSubscriptionsCard data-card-id="subscriptions"
                    :data-card-visible="getCardVisibility('subscriptions')"
                    v-if="patient && patient.id && showStripeFeatures" :patient-id="patient.id" />

                <!-- Shipments -->
                <card data-card-id="shipments" :data-card-visible="getCardVisibility('shipments')">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                        <h4 class="card-subtitle mb-0">Shipments</h4>
                        <b-checkbox v-model="showVoidShipments">Show Void ({{ voidCount }})</b-checkbox>
                    </div>
                    <a v-if="hasOldPortal" :href="oldPortalLink" target="_blank">View in Old Portal
                        <i class="fas fa-external-link-alt"></i></a>
                    <!-- Shipments -->
                    <div v-if="filteredShipments.length > 0 || shipmentPage != 0">
                        <card v-for="shipment in filteredShipments" :key="shipment.id" plain :style="{ opacity: shipment.voided ? 0.85 : 1 }">
                            <div class="d-flex justify-content-between align-items-start">
                                <div>
                                    <b>Shipped: </b>{{ convertDate(shipment.shipped ?? shipment.verified) }}
                                    <br />
                                    <b>Shipment Type: </b>{{ shipment.shipmentType }}
                                    <br />
                                    <b>Tracking: </b>
                                    <b-link :href="shipment.trackingUrl" target="_blank">{{
                                        shipment.trackingNumber
                                        }}
                                    </b-link>
                                </div>
                                <b-button size="sm" variant="primary" @click="showShippingTicket(shipment)">
                                    Shipping Ticket
                                </b-button>
                            </div>
                            <div v-if="shipment.returnDate" class="text-center bg-warning mt-2">
                                <b>RETURNED: </b>{{ convertDate(shipment.returnDate) }}
                            </div>
                            <b-badge v-if="shipment.voided" variant="danger" class="d-block mt-2">
                                VOIDED
                            </b-badge>
                        </card>
                        <BottomPagerBar
                            v-model="shipmentPage"
                            :total-rows="totalShipments"
                            :per-page="shipmentPageSize"
                            item-name="shipment"
                            show-total
                            @change-page-size="shipmentPageSize = $event"
                        />
                    </div>
                    <div v-else>No Shipments On Record</div>
                </card>

                <!-- Insurance Plans -->
                <card data-card-id="insurance-plans" :data-card-visible="getCardVisibility('insurance-plans')">
                    <b-row>
                        <b-col>
                            <!-- Insurance Plans -->
                            <h5>Insurance Plans</h5>
                            <PatientPlansComponent v-model="patient" />
                        </b-col>
                    </b-row>
                </card>

                <!-- Voicemails -->
                <PatientVoiceMailComponent data-card-id="voicemails"
                    :data-card-visible="getCardVisibility('voicemails')" :patient="patient" />

                <!-- Emergency Contacts -->
                <emergency-contact-list id="emergencyContacts" :patient="patient" data-card-id="emergency-contacts"
                    :data-card-visible="getCardVisibility('emergency-contacts')" />

                <!-- Linked Patients -->
                <emergency-contact-list id="linkedPatients" :patient="patient" :getByContactMode="true"
                    data-card-id="linked-patients" :data-card-visible="getCardVisibility('linked-patients')" />

                <!-- Allergies -->
                <card data-card-id="allergies" :data-card-visible="getCardVisibility('allergies')">
                    <h4 class="card-subtitle">Allergies</h4>
                    <div v-if="allergies && allergies.length > 0">
                        <b-table
                            :items="allergyTableData"
                            :fields="allergyTableFields"
                            striped
                            small
                            responsive="sm"
                            class="mb-0">
                        </b-table>
                    </div>
                    <div v-else>No known drug allergies</div>
                </card>

                <!-- Non-Profile Medications -->
                <card data-card-id="non-profile-medications"
                    :data-card-visible="getCardVisibility('non-profile-medications')">
                    <h4 class="card-subtitle">Non-Profile Medications</h4>
                    <b-row>
                        <b-col>
                            <!-- Non-Profile meds / Other medications -->
                            <div v-if="otherMedications && otherMedications.length > 0">
                                <div v-for="(a, index) in otherMedications" :key="otherMedications.indexOf(a)">
                                    <div v-if="a.drugName">
                                        {{ a.drugName }}
                                        <span v-if="a.startDate || a.endDate" class="text-muted">
                                            ({{ formatDateRange(a.startDate, a.endDate) }})
                                        </span>
                                        <div v-if="a.notes" class="text-muted" style="font-size: 0.9em; margin-top: 2px;">
                                            {{ a.notes }}
                                        </div>
                                    </div>
                                    <div v-else>
                                        {{ a.notes }}
                                        <span v-if="a.startDate || a.endDate" class="text-muted">
                                            ({{ formatDateRange(a.startDate, a.endDate) }})
                                        </span>
                                    </div>
                                    <hr v-if="index < otherMedications.length - 1" class="my-2" />
                                </div>
                            </div>
                            <div v-else>No known medications</div>
                        </b-col>
                    </b-row>
                </card>

                <!-- Insurance Images -->
                <card v-if="insuranceUploaded" data-card-id="insurance-upload"
                    :data-card-visible="getCardVisibility('insurance-upload')">
                    <b-row>
                        <b-col>
                            <h4 class="card-subtitle">Insurance Upload</h4>
                            <a v-b-modal="'uploadInsuranceModal'" href="#" style="color: black">
                                Uploaded: {{ insuranceUploadedDisplayDate }}
                            </a>
                        </b-col>
                    </b-row>
                </card>
                <!-- Rx Transfer History -->
                <card id="patient-rx-transfer-history-card" data-card-id="rx-transfer-history"
                    :data-card-visible="getCardVisibility('rx-transfer-history')">
                    <b-row>
                        <b-col>
                            <h4 class="card-subtitle">Transfer History</h4>
                            <RxTransferHistoryComponent :patient="patient" :page-size="5" />
                        </b-col>
                    </b-row>
                </card>

                <!-- Case History -->
                <card id="patient-case-history-card" data-card-id="case-history"
                    :data-card-visible="getCardVisibility('case-history')">
                    <b-row>
                        <b-col>
                            <h4 class="card-subtitle">Case History</h4>
                            <CasesListComponent v-if="!noCasesPermissions" :default-items="casesList" small hide-patient
                                show-status :default-page-size="10" @update="getCasesForPatient" />
                            <b-alert :show="noCasesPermissions" variant="danger" class="mt-2 mb-0">
                                <strong>
                                    You do not have permissions to view cases.
                                </strong>
                            </b-alert>
                        </b-col>
                    </b-row>
                </card>

                <!-- Follow Up History -->
                <card id="patient-follow-up-history-card" data-card-id="follow-up-history"
                    :data-card-visible="getCardVisibility('follow-up-history')">
                    <b-row>
                        <b-col>
                            <div class="mb-2">
                                <h4 class="card-subtitle mb-0">Doctor Follow Up History</h4>
                                <checkbox v-model="includeUpcomingFollowUps">Include Scheduled</checkbox>
                            </div>

                            <a v-for="c in followUpCases" href="#" @click.prevent="openFollowUpDetails(c)"
                                style="color:white !important; text-decoration: none;" :key="c.id">
                                <div :class="{
                                    'alert d-flex align-items-center': true,
                                    'alert-danger': c.status == 2,
                                    'alert-success': c.status == 3,
                                    'alert-primary': ![2, 3].includes(c.status)
                                }
                                    " role="alert">
                                    <div class="flex-fill">
                                        <div>
                                            <span v-if="c.type == faxTypeEnum.NewRxRequest"><b>{{ c.requestName }}</b> -
                                                {{
                                                    c.drugName }} </span>
                                            <span v-else><b>{{ c.requestName }}</b> - {{ c.rxID }} </span>
                                        </div>

                                        <b>{{ c.statusName }}</b> - {{ c.created | formatDate("L", false) }}
                                    </div>

                                    <b><i class="fas fa-external-link-alt"></i></b>
                                </div>
                            </a>
                        </b-col>
                    </b-row>
                </card>

                <!-- Call Log -->
                <card id="patient-call-log-card" data-card-id="call-log"
                    :data-card-visible="getCardVisibility('call-log')">
                    <b-row>
                        <b-col>
                            <h4 class="card-subtitle">Call Log</h4>
                            <PatientCallLogComponent :patient="patient" />
                        </b-col>
                    </b-row>
                </card>
            </b-col>

            <b-col cols="8">
                <!-- Alerts -->
                <EntityAlertsComponent ref="entityAlerts" :patient="patient" card />

                <!-- Orders -->
                <card data-card-id="orders" :data-card-visible="getCardVisibility('orders')">
                    <b-row>
                        <b-col>
                            <h4 class="card-subtitle">Orders</h4>
                        </b-col>
                        <b-col class="text-right" variant="primary">
                            <!-- <router-link :to="{name:'OrderDetails', params:{orderId:  props.row.id}}"> -->
                            <router-link
                                v-if="$permissions.some(p => p.permissionCodeName === 'Patient Dashboard/Create Order') && !patient.deceased"
                                :to="{
                                    name: 'Order',
                                    params: { patientId: patient.id },
                                }">
                                <span>New Order&nbsp;</span>
                                <i class="fas fa-plus-circle"></i>
                            </router-link>
                            <span v-else-if="patient.deceased" class="text-danger">
                                <i class="fas fa-exclamation-triangle"></i>
                                Patient is deceased - Orders cannot be created
                            </span>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="6">
                            <SearchComponent v-model="filterStore" :label="`Pharmacy`" :option-label="storeLabel"
                                :search-u-r-l="`/Store`" clearable prepopulate @input="loadOrders(patient.id)" />
                        </b-col>
                        <b-col class="text-right" cols="6">
                            <checkbox v-model="showCancelled" inline @change="loadOrders(patient.id)">Show Cancelled
                            </checkbox>
                            <checkbox v-model="showShipped" inline @change="loadOrders(patient.id)">Show Shipped
                            </checkbox>
                        </b-col>
                    </b-row>

                    <OrdersListComponent :orders="orders" expandable :busy="loadingOrders" :per-page="5" @change="loadOrders" />
                </card>

                <!-- Autorefill Subscriptions-->
                <card data-card-id="autorefill-subscriptions"
                    :data-card-visible="getCardVisibility('autorefill-subscriptions')">
                    <h4 class="card-subtitle">Autorefill Subscriptions</h4>
                    <RXSubscriptionsGrid ref="rxSubscriptionsGrid" v-model="patient.id"
                        :prescriptions="prescriptions" />
                </card>

                <!-- Prescriptions -->
                <card data-card-id="prescriptions" :data-card-visible="getCardVisibility('prescriptions')">
                    <div class="d-flex">
                        <h4 class="card-subtitle flex-fill">Scripts</h4>

                        <HelpPopup label="Color Codes">
                            <b-table-simple>
                                <b-tbody>
                                    <b-tr>
                                        <b-td>Refillable / Auto Refillable </b-td>
                                    </b-tr>
                                    <b-tr class="table-info">
                                        <b-td>Transfer Out</b-td>
                                    </b-tr>
                                    <b-tr class="table-danger strikethrough">
                                        <b-td>Void</b-td>
                                    </b-tr>
                                    <b-tr class="table-danger">
                                        <b-td>Expired</b-td>
                                    </b-tr>
                                    <b-tr class="table-danger font-italic">
                                        <b-td>Discontinued</b-td>
                                    </b-tr>
                                    <b-tr class="table-dark">
                                        <b-td>Not Refillable / On Hold</b-td>
                                    </b-tr>
                                    <b-tr class="table-warning">
                                        <b-td>Fill Date After Today</b-td>
                                    </b-tr>
                                </b-tbody>
                            </b-table-simple>
                        </HelpPopup>
                    </div>

                    <PrescriptionsListComponent :actionButtons="prescriptionListActionButtons"
                        :prescriptions="prescriptions" :patient="patient" colorCodePrescriptions refillBatchMode
                        showProccessQueueButton showReportProblemButton showSubscriptionButton showTransferButton
                        :per-page="50"
                        @batchFinished="onBatchFinished" @newSubscriptionSaved="loadAutoRefillData" />
                </card>

                <!--Prior Authorization-->
                <div data-card-id="prior-authorization" :data-card-visible="getCardVisibility('prior-authorization')">
                    <PriorAuthorizationListComponent v-model="patient" />
                </div>

                <!-- Electronic Prior Auth -->
                <div data-card-id="electronic-prior-auth"
                    :data-card-visible="getCardVisibility('electronic-prior-auth')">
                    <ElectronicPriorAuthorizationListPage v-model="patient" />
                </div>

                <!-- Consultations -->
                <card id="consultations-card" data-card-id="consultations"
                    :data-card-visible="getCardVisibility('consultations')">
                    <h4 class="card-subtitle">Consultations</h4>
                    <ConsultationListComponent ref="consultationList" :enable-cancel="true" :enable-edit="true"
                        :patient-id="+patient.id" :hide-patient="true" modal-id="complete-consultation-dashboard" />
                </card>

                <!-- Assistance Programs -->
                <card id="patient-assistance-program-card" data-card-id="patient-assistance-program"
                    :data-card-visible="getCardVisibility('patient-assistance-program')">
                    <div class="d-flex align-items-center">
                        <div class="flex-fill">
                            <h4 class="card-subtitle">Patient Assistance Program</h4>
                        </div>
                        <div>
                            <b-button
                                v-if="$permissions.some(p => p.permissionCodeName === 'Patient Dashboard/Create PAP')"
                                variant="success" size="sm" class="m-0" @click="showPapManualModal">
                                <i class="fa fa-plus-circle"></i>
                                Add Manually
                            </b-button>
                        </div>
                    </div>
                    <b-row>
                        <b-col cols="12">
                            <b-checkbox v-model="includeDeletedPap">Show All</b-checkbox>
                        </b-col>
                        <b-col>
                            <patient-assistance-program-table :table-filter="papFilter" ref="pap-table"
                                api-url="PatientAssistanceProgram/Patient" allow-removal />
                        </b-col>
                    </b-row>
                </card>

                <!-- Available Acceptances -->
                <card data-card-id="available-acceptances"
                    :data-card-visible="getCardVisibility('available-acceptances')">
                    <h4 class="card-subtitle">Available Acceptances</h4>
                    <AvailableAcceptanceListComponent ref="availableAcceptanceList" :patient="patient"
                        @update="addedAcceptance" />
                </card>

                <!-- Acceptance Records -->
                <card data-card-id="acceptance-records" :data-card-visible="getCardVisibility('acceptance-records')">
                    <h4 class="card-subtitle">Acceptance Records</h4>
                    <AcceptanceRecordList ref="acceptanceRecordList" :patient-id="patient.id"
                        @update="updatedAcceptance" />
                </card>

                <!-- Tasks -->
                <div data-card-id="tasks" :data-card-visible="getCardVisibility('tasks')">
                    <TasksPage :patient="patient" size="sm" :compact="true" />
                </div>
            </b-col>
        </b-row>

        <!-- Dashboard Customize Modal -->
        <DashboardCustomizeModal :cards="dashboardCards" @save-settings="saveDashboardSettings"
            @settings-reset="onSettingsReset" />

        <!-- Insurance Image Modal -->
        <b-modal id="uploadInsuranceModal" prefix="uploadInsuranceModal" hide-footer title="Upload Insurance">
            <PatientInsuranceModalComponent v-model="patient.id"></PatientInsuranceModalComponent>
        </b-modal>

        <!-- Shipping Ticket Modal -->
        <b-modal id="shippingTicketModal" ref="shippingTicketModal" hide-footer size="xl" title="Shipping Ticket">
            <ShippingTicketComponent :patient="patient" v-model="shipmentToViewInTicket" @shipment-unvoided="onShipmentUnvoided"></ShippingTicketComponent>
        </b-modal>

        <b-modal id="pap-documents" title="Patient Documents" size="xl" @ok="addPap">
            <b-row>
                <b-col cols="12" lg="6">
                    <SearchComponent v-model="selectedProgramForPap" label="Program (optional)" search-u-r-l="/Program"
                        :params="{ active: true }" clearable />
                </b-col>
                <b-col cols="12" lg="6">
                    <SearchComponent v-model="selectedDrugForPap" label="Drug (optional)" search-u-r-l="/Drug"
                        :params="{ onlyActive: true, allSources: true }" clearable />
                </b-col>
            </b-row>

            <PatientDocumentsForm :patient="patient" @selectedImageIdsUpdate="selectedPapDocuments = $event"
                selectable />
        </b-modal>

        <!-- Follow-up case details -->
        <b-modal id="followUpQueueModal" title="Doctor Follow-Up Case" size="xl" hide-footer static>
            <!-- FOLLOW UP COMPONENT -->
            <DoctorFollowUpCaseForm v-model="selectedFollowUp" @submitted="closeFollowUpModal" />
        </b-modal>

        <PatientRecentlyViewed :tracking="true" :patient="patient" />
    </div>
</template>

<script lang="ts">
import Checkbox from "@/components/Inputs/Checkbox.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import PrescriptionsListComponent from "@/components/Prescription/PrescriptionsListComponent.vue";
import OrdersListComponent from "@/components/Order/OrdersListComponent.vue";
import ShippingTicketComponent from "@/components/Shipping/ShippingTicketComponent.vue";
import NotesList from "@/components/Note/NotesList.vue";
import NotesForm from "@/components/Note/NotesForm.vue";
import { Patient, PatientProgram } from "@/models/Patient";
import { Prescription } from "@/models/Prescription";
import DoctorFollowUpCasesAlertComponent from "@/components/Doctor/DoctorFollowUpCasesAlertComponent.vue";
import { CaseDetail } from "@/models/Case";
import { Store } from "@/models/Store";
import { Order } from "@/models/Order";
import { LockHandler } from "@/mixins/LockHandler";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { DatePicker, Table, TableColumn } from "element-ui";
import { Component, Mixins, Prop, Ref, Watch } from "vue-property-decorator";
import Card from "@/components/Cards/Card.vue";
import PriorAuthorizationListComponent from "@/components/PriorAuthorization/PriorAuthorizationListComponent.vue";
import moment from "moment";
import { GSDDPatientAllergy } from "@/models/GSDD";
import PatientPlansComponent from "@/components/Insurance/PatientPlansComponent.vue";
import ConsultationListComponent from "@/components/Consultation/ConsultationListComponent.vue";
import { Shipment } from "@/models/Shipment";
import PatientDemographicsComponent from "@/components/Patient/PatientDemographicsComponent.vue";
import { FaxType, PrescriberFollowUpCase, PrescriberFollowUpStatus } from "@/models/PrescriberFollowUpCase";
import PatientInsuranceModalComponent from "@/components/Patient/PatientInsuranceModalComponent.vue";
import { PatientInsuranceUpload } from "@/models/PatientInsuranceUpload";
import RXSubscriptionsGrid from "@/components/Prescription/RXSubscriptionsGrid.vue";
import ElectronicPriorAuthorizationListPage from "@/pages/PriorAuthorization/ElectronicPriorAuthorizationListPage.vue";
import PatientAssistanceProgramTable from "@/components/PatientAssistanceProgram/PatientAssistanceProgramTable.vue";
import EmergencyContactList from "@/components/Patient/EmergencyContactList.vue";
import PatientDocumentsForm from "@/components/Patient/PatientDocumentsForm.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { EntityType } from "@/models/EntityType";
import CasesListComponent from '@/components/Cases/CasesListComponent.vue';
import HelpPopup from "@/components/HelpPopup.vue";
import { Program } from "@/models/Program";
import PatientRecentlyViewed from "@/components/Patient/PatientRecentlyViewed.vue";
import TasksPage from "../Tasks/TasksPage.vue";
import PatientCaseBanner from "@/components/Patient/PatientCaseBanner.vue";
import AcceptanceRecordList from "@/components/AcceptanceRecordList.vue";
import DoctorFollowUpCaseForm from '@/components/Doctor/DoctorFollowUpCaseForm.vue';
import PatientActionsBar from "@/components/Patient/PatientActionsBar.vue";
import AvailableAcceptanceListComponent from "@/components/Patient/AvailableAcceptanceListComponent.vue";
import PatientProgramEligibilityComponent from "@/components/Patient/PatientProgramEligibilityComponent.vue";
import { Drug } from "@/models/Drug/Drug";
import RxTransferListModalComponent from "@/components/Patient/RxTransferListModalComponent.vue";
import RxTransferHistoryComponent from "@/components/Patient/RxTransferHistoryComponent.vue";
import PatientStripeSubscriptionsCard from "@/components/Patient/PatientStripeSubscriptionsCard.vue";
import StripeProductService from "@/services/StripeProductService";
import EntityAlertsComponent from "@/components/EntityAlertsComponent.vue";
import { OtherMedicationViewModel } from "@/models/Drug/OtherMedication";
import PatientVoiceMailComponent from "@/components/Patient/PatientVoiceMailComponent.vue";
import PatientCallLogComponent from "@/components/Patient/PatientCallLogComponent.vue";
import DashboardCustomizeModal from "@/components/Patient/DashboardCustomizeModal.vue";
import { DashboardCardConfig, DashboardSettings, DEFAULT_DASHBOARD_CARDS } from "@/models/DashboardSettings";
import CardReader from "@/components/Cards/CardReader.vue";
import BottomPagerBar from "@/components/BottomPagerBar.vue";

@Component({
    components: {
        BottomPagerBar,
        CardReader,
        PatientVoiceMailComponent,
        PatientCallLogComponent,
        RxTransferListModalComponent,
        RxTransferHistoryComponent,
        DashboardCustomizeModal,
        PatientProgramEligibilityComponent,
        PatientDocumentsForm,
        ElectronicPriorAuthorizationListPage,
        SearchComponent,
        Checkbox,
        PrescriptionsListComponent,
        OrdersListComponent,
        NotesList, NotesForm,
        Card,
        [DatePicker.name]: DatePicker,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        ConsultationListComponent,
        PriorAuthorizationListComponent,
        PatientPlansComponent,
        ShippingTicketComponent,
        PatientDemographicsComponent,
        DoctorFollowUpCasesAlertComponent,
        PatientInsuranceModalComponent, RXSubscriptionsGrid,
        PatientAssistanceProgramTable,
        EmergencyContactList,
        CasesListComponent,
        HelpPopup,
        PatientRecentlyViewed,
        TasksPage,
        PatientCaseBanner,
        AcceptanceRecordList,
        DoctorFollowUpCaseForm,
        PatientActionsBar,
        AvailableAcceptanceListComponent,
        PatientStripeSubscriptionsCard,
        EntityAlertsComponent
    },
})
export default class PatientDashboardPage extends Mixins(LockHandler) {
    @Prop({ default: null }) id!: string | null;

    @Ref('doctor-followup-alerts') doctorFollowupAlerts!: DoctorFollowUpCasesAlertComponent;
    @Ref('patientActionsBar') patientActionsBar!: PatientActionsBar;

    private loadedFullPatient = false;
    private searchShouldFocus: boolean = false;
    public patient: Patient = new Patient();
    protected patientPrograms: PatientProgram[] = [];
    private prescriptions: Prescription[] = [];
    protected orders: Order[] = [];
    protected loadingOrders = false;
    protected allergies: GSDDPatientAllergy[] = [];
    private casesList: CaseDetail[] = [];
    protected loadingPatient = false;

    private showCancelled = false;
    private showShipped = false;
    protected patientShipments: Shipment[] = [];
    protected selectedProgramForPap: Program = new Program();
    protected selectedDrugForPap: Drug = new Drug();
    protected showVoidShipments: boolean = false;
    protected shipmentPage: number = 1;
    protected shipmentPageSize: number = 5;

    private filterStore: Store = {} as Store;
    private patientEntityType = EntityType.Patient;
    private latestInsuranceUpload: PatientInsuranceUpload =
        {} as PatientInsuranceUpload;
    private selectedPapDocuments: number[] = [];
    protected otherMedications: OtherMedicationViewModel[] = [];

    protected followUpCases: Array<PrescriberFollowUpCase> = new Array<PrescriberFollowUpCase>();
    protected faxTypeEnum = FaxType;

    private selectedFollowUp: PrescriberFollowUpCase = new PrescriberFollowUpCase();

    protected prescriptionListActionButtons: Array<string> = [];
    @Ref() private consultationList!: ConsultationListComponent;
    private shipmentToViewInTicket: Shipment = new Shipment();
    protected showStripeFeatures: boolean = false;

    @Ref('pap-table') private papTable!: PatientAssistanceProgramTable;

    protected includeUpcomingFollowUps = true;

    // Dashboard customization properties
    private showAllCards = false;

    get patientAlertText(): string {
        if (!this.patient?.alert) return "";
        return `Patient Memo: ${this.patient.alert}`;
    }

    get insuranceUploaded(): boolean {
        return this.latestInsuranceUpload &&
            moment(this.latestInsuranceUpload.uploaded).isValid();
    }

    get insuranceUploadedDisplayDate(): string {
        return moment(this.latestInsuranceUpload.uploaded).fromNow();
    }

    get hasOldPortal(): boolean {
        return this.patient?.createdBy?.indexOf(":") > 0;
    }

    get oldPortalLink(): string {
        if (this.patient?.createdBy?.indexOf(":") > 0) {
            const oldPatIds = this.patient?.createdBy.split(":");
            return `https://portal.cphrx1.local/Patient/${oldPatIds[0]}/${oldPatIds[1]}`;
        }
        return "";
    }

    get patientName(): string {
        return this.patient?.displayNamesForPerson() ?? "";
    }

    get voidCount(): number {
        return this.patientShipments.filter(s => s.voided).length;
    }

    get filteredShipments() {
        let shipments = this.patientShipments;
        if (!this.showVoidShipments) shipments = shipments.filter(s => !s.voided);
        const start = (this.shipmentPage - 1) * this.shipmentPageSize;
        const end = start + this.shipmentPageSize;
        return shipments.slice(start, end);
    }

    get totalShipments() {
        return this.showVoidShipments
            ? this.patientShipments.length
            : this.patientShipments.filter(s => !s.voided).length;
    }

    get openFollowUpCases() {
        return this.followUpCases.filter(c => c.status == PrescriberFollowUpStatus.Created || c.status == PrescriberFollowUpStatus.InProgress);
    }

    get allergyTableFields() {
        return [
            { key: 'allergyName', label: 'Allergy', sortable: true },
            { key: 'type', label: 'Type', sortable: true },
            { key: 'reaction', label: 'Reaction', sortable: false },
            {key: 'created', label:'Date', sortable: true, formatter: (date: any) => moment.utc(date).local().format('MM/DD/YYYY') }
        ];
    }

    get allergyTableData() {
        if (!this.allergies || this.allergies.length === 0) {
            return [];
        }

        return this.allergies.map(allergy => ({
            allergyName: allergy.allergyName,
            type: allergy.allergyType === 0 ? 'Class' : 'Ingredient',
            reaction: allergy.reaction || '-',
            created: allergy.created
        }));
    }

    // Dashboard customization computed properties

    get dashboardCards(): DashboardCardConfig[] {
        let userCards: DashboardCardConfig[] | null = null;

        // First try to get dashboard settings from global reactive state (SignalR)
        const dashboardLayout = this.$root.$data.userSettings['dashboard-layout'];
        if (dashboardLayout) {
            try {
                const settings: DashboardSettings = JSON.parse(dashboardLayout);
                userCards = settings.cards;
            } catch (error) {
                console.warn('Error parsing dashboard layout settings from SignalR:', error);
            }
        }

        // Fallback to localStorage if SignalR data not available yet
        if (!userCards) {
            const localStorageLayout = localStorage.getItem('dashboard-layout');
            if (localStorageLayout) {
                try {
                    const settings: DashboardSettings = JSON.parse(localStorageLayout);
                    userCards = settings.cards;
                } catch (error) {
                    console.warn('Error parsing dashboard layout settings from localStorage:', error);
                }
            }
        }

        // If no user settings, return defaults
        if (!userCards) {
            return structuredClone(DEFAULT_DASHBOARD_CARDS);
        }

        // Merge new cards from defaults that aren't in user's saved settings
        const mergedCards = structuredClone(userCards);
        const userCardIds = new Set(userCards.map(card => card.id));

        // Find new cards in defaults that user doesn't have
        const newCards = DEFAULT_DASHBOARD_CARDS.filter(card => !userCardIds.has(card.id));

        if (newCards.length > 0) {
            // Add new cards to the appropriate columns, maintaining their default order but placing them at the end
            const column1Cards = mergedCards.filter(card => card.column === 1);
            const column2Cards = mergedCards.filter(card => card.column === 2);

            // Calculate next order numbers for each column
            const maxColumn1Order = Math.max(0, ...column1Cards.map(card => card.order));
            const maxColumn2Order = Math.max(0, ...column2Cards.map(card => card.order));

            // Add new cards with incremented order numbers
            let nextColumn1Order = maxColumn1Order + 1;
            let nextColumn2Order = maxColumn2Order + 1;

            newCards.forEach(newCard => {
                const cardToAdd = structuredClone(newCard);
                if (cardToAdd.column === 1) {
                    cardToAdd.order = nextColumn1Order++;
                } else if (cardToAdd.column === 2) {
                    cardToAdd.order = nextColumn2Order++;
                }
                mergedCards.push(cardToAdd);
            });
        }

        return mergedCards;
    }

    get hasHiddenCards(): boolean {
        return this.dashboardCards.some(card => !card.visible);
    }

    get dashboardStyleCSS(): string {
        const styles: string[] = [];
        this.dashboardCards.forEach(card => {
            const visibility = card.visible || this.showAllCards ? 'block' : 'none';
            styles.push(`[data-card-id="${card.id}"] { order: ${card.order} !important; display: ${visibility} !important; }`);
        });
        return styles.join('\n');
    }

    private async saveDashboardSettings(settings: DashboardSettings) {
        try {
            const settingsJson = JSON.stringify(settings);

            if ((this as any).$saveUserSetting) {
                (this as any).$saveUserSetting('dashboard-layout', settingsJson);
            }

            // Also save to localStorage as backup
            localStorage.setItem('dashboard-layout', settingsJson);

            this.$set(this.$root.$data.userSettings, 'dashboard-layout', settingsJson);
        } catch (error) {
            console.error('Error saving dashboard settings:', error);
            throw error;
        }
    }

    private openCustomizeModal() {
        this.$bvModal.show('dashboardCustomizeModal');
    }

    private toggleShowAllCards() {
        this.showAllCards = !this.showAllCards;
    }

    private onSettingsReset() {
        this.$forceUpdate();
    }

    private getCardVisibility(cardId: string): string {
        const card = this.dashboardCards.find(c => c.id === cardId);
        return card ? card.visible.toString() : 'true';
    }

    async created() {
        this.prescriptionListActionButtons.push("edit");
        this.prescriptionListActionButtons.push("subscribe");
        this.prescriptionListActionButtons.push("suppress");

        // Check if Stripe products are available
        this.showStripeFeatures = await StripeProductService.hasStripeProducts();

        // If route as a PatientId as argument, load its data by default.
        if (this.id) {
            axios.get(`/Patient/${this.id}`)
                .then((response) => {
                    this.patient = new Patient(response.data);
                })
                .catch((error) => {
                    this.$notification(NotificationOptions.error(error));
                    console.error("Problem loading patient information by route Id", {
                        error,
                        response: error?.response,
                    });
                });
        } else {
            this.searchShouldFocus = true;
        }
    }

    @Watch("id")
    onParamChanged(value: string, oldValue: string) {
        if (value == oldValue) return;
        this.loadedFullPatient = false;
        this.patient = Object.assign(new Patient(), { id: value });
    }

    @Watch("patient")
    onPatientChanged(value: Patient | null) {
        const patient: Patient | null = value as Patient;

        if (!patient?.id) {
            this.$router.push({
                name: "PatientDashboard"
            });
            return;
        }

        const patientChanged = this.id != patient?.id?.toString();

        if (patientChanged) {
            this.loadedFullPatient = false;
            this.$router.push({
                name: "PatientDashboard",
                params: { id: patient.id.toString() },
            });
            return;
        }

        if (this.loadedFullPatient) return;

        this.loadingPatient = true;

        this.clearData();

        // Get the full patient object instead of the tiny stub we get from backend.
        axios
            .get(`/Patient/${patient.id}`)
            .then((response) => {
                this.patient = Object.assign(
                    new Patient(),
                    response.data,
                );
                this.loadedFullPatient = true;
                if (!this.id) {
                    this.$router.push({
                        name: "PatientDashboard",
                        params: { id: patient.id.toString() },
                    });
                }
            })
            .catch((error) => {
                console.error(
                    "Problem loading patient information its selection on the search component",
                    {
                        error,
                        response: error?.response,
                    },
                );
            }).finally(() => {
                this.loadingPatient = false;
            });

        //Get programs associated to the patient
        axios
            .get(`/Patient/${patient.id}/programs`)
            .then((response) => {
                this.patientPrograms = response.data;
            })
            .catch((error) => {
                console.error("Problem loading programs", {
                    error,
                    response: error?.response,
                });
            });

        this.loadPrescriptions();
        this.loadOrders(patient.id);
        this.fetchFollowUpCases();
        //Get patient's allergies
        axios
            .get(`Allergy/GetGSDDAllergiesForPatient/${patient.id}`)
            .then((response) => {
                this.allergies = response.data;
            })
            .catch((error) => {
                console.error("Problem loading allergies", {
                    error,
                    response: error?.response,
                });
            });

        // Get patients medications
        this.loadOtherMedications();

        this.getCasesForPatient();

        this.loadShipments();

        //getLatestInsuranceUpload
        axios
            .get<PatientInsuranceUpload>(
                `/PatientInsuranceUpload/GetLatest/${patient.id}`,
            )
            .then((response) => {
                this.latestInsuranceUpload = response.data;
                this.latestInsuranceUpload.uploaded;
            });
    }

    // Force refresh patient data - used when child components update patient info
    refreshPatientData(updatedPatient?: Patient) {
        if (updatedPatient) {
            // If we receive updated patient data from a child component, use it directly
            this.patient = updatedPatient;
        } else {
            // Otherwise, fetch fresh data from the server
            this.loadingPatient = true;
            axios
                .get(`/Patient/${this.patient.id}`)
                .then((response) => {
                    this.patient = Object.assign(new Patient(), response.data);
                })
                .catch((error) => {
                    console.error("Problem refreshing patient data", {
                        error,
                        response: error?.response,
                    });
                    this.$notification(NotificationOptions.error("Failed to refresh patient data"));
                })
                .finally(() => {
                    this.loadingPatient = false;
                });
        }
    }

    protected noCasesPermissions = false;

    async getCasesForPatient() {
        try {
            if (!this.patient?.id) return;
            const response = await axios.get(`/Patient/${this.patient.id}/Cases`);
            this.casesList = response.data;
            this.noCasesPermissions = false;
        } catch (error) {
            const err = error as AxiosError;
            this.noCasesPermissions = err.status === 403;
            console.error("Problem loading insurance Patient Cases", error, err.status, err.status === 403);
        }
    }

    openFollowUpDetails(c: PrescriberFollowUpCase) {
        this.selectedFollowUp = c;
        this.$bvModal.show('followUpQueueModal');
    }

    clearData() {
        this.patientPrograms = [];
        this.prescriptions = [];
        this.orders = [];
        this.loadingOrders = false;
        this.allergies = [];
        this.otherMedications = [];
        this.followUpCases = [];
        let rxSubscriptionsGrid = this.$refs
            .rxSubscriptionsGrid as RXSubscriptionsGrid;
        if (rxSubscriptionsGrid) rxSubscriptionsGrid.clearData();
    }

    clearClick() {
        this.clearData();
        this.patient = new Patient();
    }

    loadPrescriptions() {
        if (!this.patient?.id) return;

        //Get patient's prescriptions and format the data to be shown in the grid.
        axios
            .get(`/Patient/${this.patient.id}/prescriptions`)
            .then((response) => {
                this.prescriptions = response.data.map((rx: Prescription) => new Prescription(rx));
                this.loadRxsAvailableForAutorefill();
            })
            .catch((error) => {
                console.error("Problem loading prescriptions", {
                    error,
                    response: error?.response,
                });
            });
    }

    onBatchFinished() {
        // Reload prescriptions to reflect the newly processed refill
        this.loadPrescriptions();

        // Refresh the PatientActionsBar status indicators so NPC text warning logic uses current data
        if (this.patientActionsBar) {
            this.patientActionsBar.refreshStatusIndicators();
        }
    }

    protected includeDeletedPap:boolean = false;
    get papFilter(): AxiosRequestConfig {
        let filter: AxiosRequestConfig;
        filter = {
            params: {
                id: this.patient.id,
                includeDeleted: this.includeDeletedPap,
            },
        };
        return filter;
    }

    loadRxsAvailableForAutorefill() {
        if (!this.patient?.id) return;
        this.$http
            .get<Array<Prescription>>(
                `autorefill/available-for-autorefill/${this.patient.id}`,
            )
            .then((res) => {
                if (res.data?.length) {
                    const availableRxsForAutorefill = res.data.map((rx) =>
                        Object.assign(new Prescription(), rx),
                    );
                    this.prescriptions.forEach((rx) => {
                        rx.isAvailableForAutorefill =
                            availableRxsForAutorefill.some(
                                (rx2) => rx2.rxID == rx.rxID,
                            );
                    });
                }
            })
            .catch((err) => {
                console.error(
                    "Error loading Rxs available for subscription to auto refill.",
                    { err, response: err?.response },
                );
            });
    }

    loadAutoRefillData() {
        this.loadRxsAvailableForAutorefill();
        let rxSubscriptionsGrid = this.$refs
            .rxSubscriptionsGrid as RXSubscriptionsGrid;
        if (rxSubscriptionsGrid) rxSubscriptionsGrid.loadData();
    }

    loadOrders(patientId?: string | number) {
        patientId = patientId || this.patient?.id;
        let storeId =
            this.filterStore?.id == 0 || this.filterStore?.id
                ? this.filterStore?.id
                : "";
        //Get patient's prescriptions and format the data to be shown in the grid.
        this.loadingOrders = true;
        axios
            .get(
                `/Patient/${patientId}/orders/${this.showCancelled}/${this.showShipped}/${storeId}`,
            )
            .then((response) => {
                this.orders = response.data;
                this.orders.sort((a: Order, b: Order) => {
                    return (
                        new Date(b.created).getTime() -
                        new Date(a.created).getTime()
                    );
                });
            })
            .catch((error) => {
                console.error("Problem loading patient orders", {
                    error,
                    response: error?.response,
                });
            })
            .finally(() => {
                this.loadingOrders = false;
            });
    }

    loadShipments() {
        if (!this.patient?.id) return;

        axios
            .get(`/Shipping/GetShipmentsByPatient/${this.patient.id}`)
            .then((response) => {
                this.patientShipments = response.data;
            })
            .catch((error) => {
                console.error("Error Getting Shipments", {
                    error,
                    response: error?.response,
                });
            });
    }

    async loadOtherMedications() {
        if (!this.patient?.id) {
            this.otherMedications = [];
            return;
        }
        try {
            const resp = await axios.get<OtherMedicationViewModel[]>("/api/OtherMedication", { params: { patientId: this.patient.id } });
            this.otherMedications = resp.data;
        } catch (err) {
            console.error(err);
            this.otherMedications = [];
        }
    }

    refreshNotesList() {
        if (this.$refs.patientNotesList instanceof NotesList)
            (this.$refs.patientNotesList as any).loadNotes();
    }

    storeLabel(object: Store) {
        let res = "";
        if (object.name) res = object.name;

        return res;
    }

    convertDate(date: string | Date) {
        return moment.utc(date).format("L");
    }

    formatDateRange(startDate: any, endDate: any) {
        const format = (d: string | Date | null) => d && moment(d).format('MM/DD/YYYY');
        const start = format(startDate);
        const end = format(endDate);

        return start && end ? `${start} - ${end}`
            : start ? `Started: ${start}`
                : end ? `Ended: ${end}`
                    : '';
    }

    updateConsultationsList() {
        (this.consultationList as any).getConsultation();
        (this.$refs.entityAlerts as EntityAlertsComponent)?.refresh();
    }

    showShippingTicket(shipment: Shipment) {
        this.shipmentToViewInTicket = new Shipment(shipment);
        this.$bvModal.show("shippingTicketModal");
    }

    onShipmentUnvoided(unvoidedShipment: Shipment) {
        // Reload the shipments list to reflect the updated voided status
        this.loadShipments();

        // Update the shipment being viewed in the modal
        this.shipmentToViewInTicket = new Shipment(unvoidedShipment);
    }

    showPapManualModal() {
        this.selectedProgramForPap = new Program();
        this.selectedDrugForPap = new Drug();
        this.$bvModal.show("pap-documents");
    }

    async addPap(event: any) {
        event.preventDefault();

        if (!this.selectedPapDocuments.length) {
            this.$notification(NotificationOptions.error("You must select a document to create this manual PAP record."));
            return;
        }

        try {
            const data = this.selectedPapDocuments.map(documentId => {
                return { id: documentId };
            });
            const programId = this.selectedProgramForPap?.id;
            const drugSource = this.selectedDrugForPap?.source;
            const packageId = this.selectedDrugForPap?.drugId;

            await axios.post(`api/PatientAssistanceProgram/Create/${this.patient.id}`, data, {
                params: {
                    programId: programId > 0 ? programId : null,
                    drugSource,
                    packageId: packageId > 0 ? packageId : null
                },
            });

            this.$notification(NotificationOptions.successSaveNotificationPreset("Patient Assistance Program"));

            this.$nextTick(() => {
                this.$bvModal.hide("pap-documents");
            });
        } catch (err) {
            console.warn("err: ", err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.selectedPapDocuments = [];
            this.papTable.clearAndRefresh();
        }
    }

    @Watch("includeUpcomingFollowUps")
    fetchFollowUpCases() {
        if (!this.patient?.id) return;
        //Check if there are pending follow up cases
        this.$http.get<Array<PrescriberFollowUpCase>>(`/prescriber/follow-up-by-patient/${this.patient.id}`,
            { params: { includeUpcoming: this.includeUpcomingFollowUps } })
            .then(res => {
                if (res.data?.length) {
                    this.followUpCases = res.data.map(c => new PrescriberFollowUpCase(c));
                } else {
                    this.followUpCases = [];
                }
            })
            .catch(err => console.error('Error while getting doctor follow-up cases by patient.', {
                err,
                response: err?.response
            }));
        //this.doctorFollowupAlerts?.fetchFollowUpCases();
    }

    closeFollowUpModal() {
        this.$bvModal.hide('followUpQueueModal');
        this.fetchFollowUpCases();
    }

    scrollCaseHistoryIntoView() {
        document.getElementById('patient-case-history-card')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    @Ref("acceptanceRecordList") protected acceptanceRecordList!: AcceptanceRecordList;
    @Ref("availableAcceptanceList") protected availableAcceptanceList!: AvailableAcceptanceListComponent;

    addedAcceptance() {
        this.acceptanceRecordList.getRecords();
    }

    updatedAcceptance() {
        this.availableAcceptanceList.loadAvailableAcceptances();
    }
}

</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
.form-check>.form-check-label {
    color: black;
}

/* Enable flexbox for dashboard card ordering */
.patient-dashboard-view .col-4,
.patient-dashboard-view .col-8 {
    display: flex;
    flex-direction: column;
}

/* Smooth transitions when reordering cards */
.patient-dashboard-view [data-card-id] {
    transition: order 0.3s ease-in-out;
}

::v-deep(.strikethrough td) {
    text-decoration: line-through;
}

.patient-name.glass-pane {
    position: sticky;
    top: 0;
    z-index: 190;
}

/* .card.titlebar {
    position: sticky;
    top: -95px;
    z-index: 500;
} */

/* Dashboard Customization Styles */
.patient-dashboard-view .col-4,
.patient-dashboard-view .col-8 {
    display: flex;
    flex-direction: column;
}

.show-all-cards [data-card-id].card,
.show-all-cards [data-card-id]:not(.card) .card {
    border: 2px dashed rgb(184, 184, 184);
}

.show-all-cards [data-card-id].card[data-card-visible="true"],
.show-all-cards [data-card-id]:not(.card)[data-card-visible="true"] .card {
    border: 1px solid rgba(0, 0, 0, 0.125);
}
</style>
